// React component that draws a foreground and background image on a p5.js canvas.
// The foreground image is drawn on top of the background image.

import React, { useEffect } from "react";

import { CanvasMode, createSite } from "./createSiteVisualisation";

import { RequestQuote } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import { getAllData } from "../../libs/firebase/firestore/getAllData";
import { getData } from "../../libs/firebase/firestore/getData";
import { updateData } from "../../libs/firebase/firestore/updateData";
import { uploadData } from "../../libs/firebase/firestore/uploadData";
import AppBarDesignerCreateSite from "../AppBar/Designer/CreateSite";
import AppBarDesignerEditSite from "../AppBar/Designer/EditSite";
import ToolbarDesignerCreatingPolygon from "../Toolbar/Designer/CreatingPolygon";
import ToolbarDesignerDefault from "../Toolbar/Designer/Default";
import ToolbarDesignerEditingPolygon from "../Toolbar/Designer/EditingPolygon";
import Color from "./Colour";
import PlantList from "./PlantList";
import QuoteMulch from "./Quote/Mulch";
import { polygonsFromData } from "./createPolygon";
import { PolygonTypes } from "./polygonTypes";
import EventFob from "./Event/Fob";
import ControlsZoom from "./Controls/Zoom";

function getAppBar(mode, onSave, clients, client, siteName) {
  switch (mode) {
    case CanvasMode.CREATE:
      return <AppBarDesignerCreateSite onSaveSite={onSave} clients={clients} />;
    case CanvasMode.EDIT:
      return (
        <AppBarDesignerEditSite
          client={client}
          siteName={siteName}
          onSaveSite={onSave}
          clients={clients}
        />
      );

    default:
      return null;
  }
}

const Site = ({}) => {
  const p5Container = React.createRef();
  // state to store the visualisation
  const [visualisation, setVisualisation] = React.useState(null);

  const [polygons, setPolygons] = React.useState([]);

  const [currentPolygon, setCurrentPolygon] = React.useState(null);

  // state for clientName
  const [client, setClient] = React.useState(null);

  // state for show plant list
  const [showPlantList, setShowPlantList] = React.useState(false);

  // state show quote
  const [showQuote, setShowQuote] = React.useState(false);

  // state for plants
  const [plants, setPlants] = React.useState([]);

  // state for siteName
  const [siteName, setSiteName] = React.useState(null);

  const { siteId } = useParams();
  // state for clients
  const [clients, setClients] = React.useState([]);
  // store visualisation as ref
  const visualisationRef = React.useRef(visualisation);

  const onPolygonsChange = (polygons) => {
    setPolygons([...polygons]);
  };

  const handlePolygonsUpdated = (polygons) => {
    visualisationRef.current.setPolygons(polygons);
    setPolygons([...polygons]);
  };

  const handleDeleteCurrentPolygon = () => {
    // Delete selected vertex
    visualisationRef.current.onDeleteVertex();
    // setCurrentPolygon(null);
  };

  const handleCancelCurrentPolygon = () => {
    visualisationRef.current.onCancel();
    setCurrentPolygon(null);
  };

  const handleDeleteAllCurrentPolygon = () => {
    visualisationRef.current.onDelete();
    setCurrentPolygon(null);
  };

  const handleCompleteCurrentPolygon = () => {
    visualisationRef.current.onComplete();
    setCurrentPolygon(null);
  };

  const handleCurrentPolygonChange = (polygon) => {
    // THis is a polygon in the middle of being created
    setCurrentPolygon(polygon);
  };

  const handleZoomIn = () => {
    visualisationRef.current.zoomIn();
  };

  const handleZoomOut = () => {
    visualisationRef.current.zoomOut();
  };

  const handleRequestQuote = () => {
    setShowQuote(true);
  };

  useEffect(() => {
    let newVis = createSite(
      p5Container.current,
      onPolygonsChange,
      handleCurrentPolygonChange,
      plants,
      {}
    );
    setVisualisation(newVis);
    visualisationRef.current = newVis;

    // if site id is present, get the site data
    if (siteId) {
      // get site data
      getData(
        `/sites/${siteId}`,
        (data) => {
          let polys = polygonsFromData(data.polygons);
          visualisationRef.current.setPolygons(polys);
          console.log(polys);
          if (polys.length) {
            // center the screens
            // Check if there is a polygon of type Border
            let polygonToCenterOn = polys.find(
              (polygon) => polygon.type() === PolygonTypes.BORDER
            );
            if (!polygonToCenterOn) {
              // just use the first polygon
              polygonToCenterOn = polys[0];
            }

            let { x, y } = polygonToCenterOn.centroid();
            let pixelCenter = visualisationRef.current.pixelCenter();
            x = pixelCenter.x - x;
            y = pixelCenter.y - y;
            visualisationRef.current.setCenter({ x, y });
          }

          // set client
          setClient(data.clientName);
          // set site name
          setSiteName(data.name);
        },
        (error) => {
          console.log("error getting site data", error);
        }
      );
    }

    // get clients
    getAllData(
      "clients",
      (data) => {
        // console.log("got clients", data);
        setClients(data);
      },
      (error) => {
        console.log("error getting clients", error);
      }
    );

    // get plants
    getAllData(
      "plants",
      (data) => {
        // console.log("got plants", data);
        // ignore plants without a name
        data = data.filter((plant) => plant.name);

        setPlants(data);
      },
      (error) => {
        console.log("error getting plants", error);
      }
    );
    return () => {
      if (visualisationRef.current) {
        visualisationRef.current.destroy();
        visualisationRef.current = null;
        newVis = null;
      }
    };
  }, []);
  const handleCreatePlant = (plantName, successCb) => {
    getData(
      "plants/all",
      (data) => {
        let newPlants = [...data.names, plantName];
        updateData(
          {
            names: newPlants,
          },
          "plants",
          "all",
          () => {
            getAllData(
              "plants",
              (data) => {
                // console.log("got plants", data);
                // ignore plants without a name
                data = data.filter((plant) => plant.name);

                setPlants(data);
                successCb();
              },
              (error) => {
                console.log("error getting plants", error);
              }
            );
          },
          (error) => {
            console.log(error);
          }
        );
      },
      (error) => {
        console.log(error);
      }
    );
  };

  const handleOnSiteSave = (info, successCb, failCb) => {
    let polygonData = polygons.map((polygon) => {
      return polygon.toData();
    });

    // if polygon data is empty, fail
    if (!polygonData.length) {
      failCb("Please create a site before saving");
      return;
    }

    // save to firestore
    if (siteId) {
      let request = {
        polygons: polygonData,
      };

      updateData(
        request,
        "sites",
        siteId,
        (data) => {
          console.log("site updated", data);
          successCb();
        },
        (error) => {
          console.log("error updating site", error);
          failCb(error);
        }
      );
    } else {
      let request = {
        ...info,
        polygons: polygonData,
      };
      uploadData(
        request,
        "sites",
        (data) => {
          console.log("site saved", data);
          successCb();
        },
        (error) => {
          console.log("error saving site", error);
          failCb(error);
        }
      );
    }
  };

  // get the selected polygon
  const selectedPolygon = polygons.find((polygon, index) => polygon.selected());

  let siteMode = (siteId && CanvasMode.EDIT) || CanvasMode.CREATE;

  const handleOnPlants = () => {
    setShowPlantList(!showPlantList);
  };

  let globalPlants = [];

  polygons.forEach((polygon) => {
    if (polygon.plants() && polygon.plants().length > 0) {
      globalPlants = [...globalPlants, ...polygon.plants()];
    }
  });
  const selectedPlant = visualisationRef.current
    ? visualisationRef.current.selectedPlant()
    : null;

  const mPerPixel = visualisationRef.current
    ? visualisationRef.current.mPerPixel()
    : 0;
  const handleMPerPixelChange = (mPerPixel) => {
    visualisationRef.current.setMPerPixel(mPerPixel);
    setCurrentPolygon([...currentPolygon]);
  };

  return (
    <Box
      style={{
        height: `calc(100vh - 64px)`,
        width: "100%",
      }}
    >
      {getAppBar(siteMode, handleOnSiteSave, clients, client, siteName)}

      <Box
        style={{
          height: "100%",
          width: "100%",
          minHeight: "200px",
          overflow: "hidden",
        }}
        position="relative"
      >
        <EventFob siteUid={siteId} />
        {/* <Box
          style={{
            position: "absolute",
            top: "4rem",
            right: "1rem",
            zIndex: 1000,

            backgroundColor: "rgba(255,255,255,1)",

            border: "1px solid rgba(0,0,0,0.4)",
            borderRadius: "1rem",
          }}
        >
          <ControlsZoom onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
        </Box> */}
        <Box
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            zIndex: 1000,

            backgroundColor: "rgba(255,255,255,1)",
            borderRadius: "1rem",
            // padding: ".5rem 1rem",
            width: "fit-content",
          }}
        >
          <IconButton
            style={{
              border: "1px solid rgba(0,0,0,0.4)",
              borderRadius: "1rem",
            }}
            onClick={handleRequestQuote}
          >
            <RequestQuote />
          </IconButton>
        </Box>
        <Box
          style={{
            // top center absolute
            position: "absolute",
            top: "1rem",
            left: "50%",
            transform: "translate(-50%, 0)",
            width: "100%",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,1)",
            borderRadius: "1rem",
            padding: ".5rem 1rem",
            width: "fit-content",
            border: "1px solid rgba(0,0,0,0.4)",
          }}
        >
          {/* <FlowerTime plants={globalPlants || []} allPlants={plants} /> */}
          <Color plants={globalPlants || []} allPlants={plants} />
        </Box>
        <div
          style={{
            height: "100%",
            width: "100%",
            touchAction: "none",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          ref={p5Container}
        />
        <ToolbarDesignerDefault
          open={
            !visualisationRef.current || visualisationRef.current.isEditMode()
          }
          onCreate={() => {
            visualisationRef.current.setCreateMode();
          }}
          onCreateLine={() => {
            visualisationRef.current.setCreateLineMode();
          }}
        />
        <ToolbarDesignerCreatingPolygon
          polygon={currentPolygon}
          mPerPixel={mPerPixel}
          onMPerPixelChange={handleMPerPixelChange}
          open={
            visualisationRef.current && visualisationRef.current.isCreateMode()
          }
          onComplete={handleCompleteCurrentPolygon}
          onCancel={handleCancelCurrentPolygon}
          onDelete={handleDeleteCurrentPolygon}
          onDeleteAll={handleDeleteAllCurrentPolygon}
        />
        <ToolbarDesignerEditingPolygon
          name={selectedPolygon ? selectedPolygon.name() : ""}
          type={selectedPolygon ? selectedPolygon.type() : null}
          locked={selectedPolygon ? selectedPolygon.locked() : false}
          onDelete={() => {
            // Delete selected vertex
            visualisationRef.current.onDeleteVertex();
          }}
          onDeleteAll={() => {
            let polygonIndex = polygons.findIndex((polygon) =>
              polygon.selected()
            );
            if (polygonIndex !== -1) {
              polygons.splice(polygonIndex, 1);
              handlePolygonsUpdated(polygons);
            }
          }}
          onToggleLock={() => {
            if (selectedPolygon) {
              selectedPolygon.toggleLock();
            }
            /// update polygons
            handlePolygonsUpdated(polygons);
          }}
          onTypeChange={(type) => {
            if (selectedPolygon) {
              selectedPolygon.setType(type);
            }
            /// update polygons
            handlePolygonsUpdated(polygons);
          }}
          onNameChange={(name) => {
            if (selectedPolygon) {
              selectedPolygon.setName(name);
            }
            /// update polygons
            handlePolygonsUpdated(polygons);
          }}
          onPlants={handleOnPlants}
        />
      </Box>
      <PlantList
        open={showPlantList}
        onClose={() => {
          setShowPlantList(false);
        }}
        plants={selectedPolygon ? selectedPolygon.plants() : []}
        allPlants={plants}
        title={!selectedPolygon ? "Plants" : selectedPolygon.name()}
        onAddPlant={(plant) => {
          selectedPolygon.addPlant(plant);
          handlePolygonsUpdated(polygons);
        }}
        onCreatePlant={handleCreatePlant}
        onDeletePlant={(plant) => {
          selectedPolygon.removePlant(plant);
          handlePolygonsUpdated(polygons);
        }}
        selectedPlant={selectedPlant}
        onPlantClick={(plant) => {
          console.log("plant clicked", plant);
          // toggle selected plant
          if (selectedPlant && selectedPlant === plant) {
            visualisationRef.current.selectPlant(null);
          } else {
            visualisationRef.current.selectPlant(plant);
          }
          handlePolygonsUpdated(polygons);
        }}
        siteUid={siteId}
      />
      <QuoteMulch
        onClose={() => {
          setShowQuote(false);
        }}
        areas={polygons}
        open={showQuote}
        mPerPixel={mPerPixel}
      />
    </Box>
  );
};

export default Site;
